import {
  Input as AntdInput,
  Button,
  Card,
  DatePicker,
  Modal,
  Spin,
  Upload,
  notification,
} from "antd";
import { Select, Input, Button as MantineButton } from "@mantine/core";
import {
  IconArrowLeft,
  IconArrowRight,
  IconCalendar,
  IconFileText,
  IconFolder,
  IconMap,
  IconMapPin,
  IconPackage,
  IconUpload,
  IconUser,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import dayjs from "dayjs";
import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { useData } from "@/utility/state/contextAPI";
import { useTranslation } from "react-i18next";

function EditQuotationPage() {
  const { t } = useTranslation();
  const [quotationData, setQuotationData] = useState({
    date: "",
    remarks: "",
    partner: "",
    partnerAddress: {
      city: null,
      district: null,
      postalCode: null,
      address: null,
    },
    projectStatus: "quotation",
    name: "",
    company: localStorage.getItem("companyId"),
  });
  const [editQuotationData, setEditQuotationData] = useState({});
  const [fileUploadState, setFileUploadState] = useState(false);
  const [contractUploadState, setContractUploadState] = useState(false);
  const [newFileUpload, setNewFileUpload] = useState([]);
  const [contractFileUpload, setContractFileUpload] = useState([]);
  const [partnerData, setPartnerData] = useState([]);
  const [api, contextHolder] = notification.useNotification();
    const [basicInfoExpanded] = useState(true);
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [folderSelectModalOpen, setFolderSelectModalOpen] = useState(false);
  const [pendingUploadFiles, setPendingUploadFiles] = useState<File[]>([]);
  const FOLDERS = [
    { id: "A", name: "A. 構件" },
    { id: "B", name: "B. 零件" },
    { id: "C", name: "C. 安裝平立面圖" },
    { id: "D", name: "D. 建築平立方圖（設計端）" },
    { id: "E", name: "E. 結構平立方圖（設計端）" },
    { id: "F", name: "F. 材質證明" },
    { id: "G", name: "G. 無輻射證明" },
    { id: "H", name: "H. 會議記錄" },
    { id: "I", name: "I. 注意事項" },
  ];

  const getFilesInFolder = (folder: string) => {
    if (!Array.isArray(newFileUpload)) return [];
    return newFileUpload.filter((file: any) => file?.folder === folder);
  };

  const { id } = useParams();
  const navigate = useNavigate();

  const { date, remarks, partner, partnerAddress, name } = quotationData || {};
  const { city, district, postalCode, address } = partnerAddress || {};

  const handleDataChange = (name, value) => {
    setQuotationData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    setEditQuotationData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleChangePartnerData = (value, partnerData) => {
    let match = value.match(/ID:\s*(\d+)/);
    let idNumber = match ? match[1] : null;

    let selectedData = partnerData.filter((e) => e?.id == idNumber)[0];

    handleDataChange("partner", selectedData);
  };

  const handleSubmit = async ({ data }) => {
    try {
      const result = await single(
        orpcClient.project.update([
          {
            id: id!,
            ...data,
          },
        ])
      );
      if (result) {
        navigate(`/hrMainPage/projectQuotation/${id}`);
        return;
      }
    } catch (error) {
      console.error("Error post quotation data:", error);
      return api.error({
        message: t("hrMain.editFailed"),
        description: <div>{error?.message}</div>,
        placement: "topLeft",
        duration: 0,
      });
    }
  };

  const handleFirstLayerUpload = (info: any) => {
    const files = info.fileList.map((f: any) => f.originFileObj).filter(Boolean);
    setPendingUploadFiles(files);
    setFolderSelectModalOpen(true);
  };

  const handleModalFolderSelectAndUpload = async (folderId: string) => {
    if (!pendingUploadFiles.length) return;
    setFolderSelectModalOpen(false);
    setFileUploadState(true);
    const uploaded: any[] = [];
    for (const file of pendingUploadFiles) {
      try {
        const sasUrlData = await orpcClient.storage.createBlobSasUrl({
          fileName: file.name,
          contentType: file.type,
        });
        const uploadResponse = await fetch(sasUrlData.blobSasUrl, {
          method: "PUT",
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": file.type,
          },
          body: file,
        });
        if (uploadResponse.ok) {
          uploaded.push({ id: Date.now() + Math.random(), name: file.name, url: sasUrlData.blobUrl, folder: folderId });
        } else {
          api.error({ message: t("quotation.uploadErrorTitle"), description: <div>{t("quotation.fileUploadFailed")}</div>, placement: "topLeft", duration: 3000 });
        }
      } catch (error) {
        api.error({ message: t("quotation.uploadErrorTitle"), description: <div>{(error as any)?.message || t("quotation.fileUploadFailed")}</div>, placement: "topLeft", duration: 3000 });
      }
    }
    if (uploaded.length) {
      // 先寫回 project 成功才更新畫面，否則會出現「說成功但重新整理就消失」
      const updated = [
        ...(Array.isArray(newFileUpload) ? (newFileUpload as any[]) : []),
        ...uploaded,
      ];
      try {
        await single(
          orpcClient.project.update([{ id: id!, attachment: updated }]),
        );
        setNewFileUpload(updated as any);
        api.success({ message: t("quotation.uploadSuccess"), description: <div>{t("quotation.fileUploadedSuccessfully")}</div>, placement: "topLeft", duration: 2000 });
      } catch (error) {
        console.error("Error saving attachment:", error);
        api.error({
          message: t("quotation.uploadErrorTitle"),
          description: (
            <div>
              {(error as any)?.message || t("quotation.fileUploadFailed")}
            </div>
          ),
          placement: "topLeft",
          duration: 3000,
        });
      }
    }
    setFileUploadState(false);
    setPendingUploadFiles([]);
  };

  const handleFolderUpload = async (file: File) => {
    if (!file || !currentFolder) return;
    setFileUploadState(true);
    try {
      const sasUrlData = await orpcClient.storage.createBlobSasUrl({
        fileName: file.name,
        contentType: file.type,
      });
      const uploadResponse = await fetch(sasUrlData.blobSasUrl, {
        method: "PUT",
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": file.type,
        },
        body: file,
      });
      if (uploadResponse.ok) {
        const newFileObject = {
          id: Date.now(),
          name: file.name,
          url: sasUrlData.blobUrl,
          folder: currentFolder,
        };
        // 先寫回 project 成功才更新畫面，否則會出現「說成功但重新整理就消失」
        const updated = [
          ...(Array.isArray(newFileUpload) ? (newFileUpload as any[]) : []),
          newFileObject,
        ];
        await single(
          orpcClient.project.update([{ id: id!, attachment: updated }]),
        );
        setNewFileUpload(updated as any);
        api.success({
          message: t("quotation.uploadSuccess"),
          description: <div>{t("quotation.fileUploadedSuccessfully")}</div>,
          placement: "topLeft",
          duration: 2000,
        });
      } else {
        api.error({
          message: t("quotation.uploadErrorTitle"),
          description: <div>{t("quotation.fileUploadFailed")}</div>,
          placement: "topLeft",
          duration: 3000,
        });
      }
    } catch (error) {
      api.error({
        message: t("quotation.uploadErrorTitle"),
        description: <div>{(error as any)?.message || t("quotation.fileUploadFailed")}</div>,
        placement: "topLeft",
        duration: 3000,
      });
    }
    setFileUploadState(false);
  };

  const handleChangeContractImg = async (info) => {
    setContractUploadState(true);
    if (!contractUploadState) {
      const file = info.file.originFileObj;

      try {
        const sasUrlData = await orpcClient.storage.createBlobSasUrl({
          fileName: file.name,
          contentType: file.type,
        });

        const uploadResponse = await fetch(sasUrlData.blobSasUrl, {
          method: "PUT",
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": file.type,
          },
          body: file,
        });

        if (uploadResponse.ok) {
          const newContractFile = {
            id: Date.now(),
            name: file.name,
            url: sasUrlData.blobUrl,
          };

          await single(
            orpcClient.project.update([
              {
                id: id!,
                contractFile: newContractFile,
              },
            ])
          );

          setContractFileUpload([newContractFile]);

          api.success({
            message: t("quotation.uploadSuccess"),
            description: <div>{t("quotation.contractUploadedSuccessfully")}</div>,
            placement: "topLeft",
            duration: 2000,
          });
        } else {
          console.error("File upload failed:", uploadResponse.statusText);
          api.error({
            message: t("quotation.uploadErrorTitle"),
            description: <div>{t("quotation.contractUploadFailed")}</div>,
            placement: "topLeft",
            duration: 3000,
          });
        }
      } catch (error) {
        console.error("Error uploading contract file:", error);
        api.error({
          message: t("quotation.uploadErrorTitle"),
          description: (
            <div>{error?.message || t("quotation.contractUploadFailed")}</div>
          ),
          placement: "topLeft",
          duration: 3000,
        });
      }
      setContractUploadState(false);
    }
  };

  const handleChangeAddressData = (value, type) => {
    setQuotationData((prevData) => ({
      ...prevData,
      partnerAddress: {
        ...(prevData?.partnerAddress || {}),
        [type]: value,
      },
    }));

    setEditQuotationData((prevData) => ({
      ...prevData,
      partnerAddress: {
        ...(prevData?.partnerAddress || {}),
        [type]: value,
      },
    }));
  };

  const openGoogleMaps = () => {
    const fullAddress = `${city || ""}${district || ""}${postalCode || ""}${address || ""}`;
    const encodedAddress = encodeURIComponent(fullAddress);
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    window.open(googleMapsUrl, "_blank");
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await orpcClient.partner.list({});
        setPartnerData(result);
      } catch (error) {
        console.error("Error fetching partner data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await single(
          orpcClient.project.read([{ id: id! }])
        );
        if (!result) return;

        setQuotationData(result as any);
        const attachment = result?.attachment;
        const normalizedAttachment = attachment
          ? Array.isArray(attachment)
            ? attachment.map((item, index) =>
                typeof item === "string"
                  ? {
                      id: Date.now() + index,
                      name: item.split("/").pop() || item,
                      url: item,
                    }
                  : item
              )
            : typeof attachment === "string"
              ? [
                  {
                    id: Date.now(),
                    name: attachment.split("/").pop() || attachment,
                    url: attachment,
                  },
                ]
              : [attachment]
          : [];
        setNewFileUpload(normalizedAttachment);
        setContractFileUpload(
          result?.contractFile ? [result?.contractFile] : []
        );
      } catch (error) {
        console.error("Error fetching project data:", error);
      }
    };

    fetchData();
  }, [id]);


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 m-5">
      {contextHolder}
      <div className="container mx-auto px-6 md:px-12 lg:px-16 py-8 max-w-7xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ color: "#6F6F6F" }}>
            {t("quotation.edit") || "編輯專案"}
          </h1>
          <p className="text-gray-400 text-lg">
            {t("quotation.managePartnersAndOrders") || "管理合作夥伴與訂單資訊"}
          </p>
        </div>

        <Card className="mb-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center justify-between border-b pb-3 mb-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center">
                <IconFileText size={16} style={{ color: "#1890ff" }} />
              </div>
              <h2
                className="text-lg font-semibold"
                style={{ color: "#6F6F6F" }}
              >
                {t("quotation.basicInfo") || "基本資訊"}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="text"
                shape="circle"
                icon={<IconMap size={16} />}
                onClick={openGoogleMaps}
                className="hover:bg-blue-50"
                title={t("quotation.openGoogleMaps") || "在 Google 地圖中開啟"}
              />
            </div>
          </div>

          {basicInfoExpanded && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <label
                    className="text-xs font-semibold flex items-center gap-1.5"
                    style={{ color: "#6F6F6F" }}
                  >
                    <IconUser size={14} style={{ color: "#1890ff" }} />
                    {t("quotation.name") || "名稱"}
                  </label>
                  <Input
                    value={name}
                    onChange={({ target: { value } }) =>
                      handleDataChange("name", value)
                    }
                    placeholder={t("quotation.namePlaceholder") || "請輸入名稱"}
                    className="h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <label
                    className="text-xs font-semibold flex items-center gap-1.5"
                    style={{ color: "#6F6F6F" }}
                  >
                    <IconCalendar size={14} style={{ color: "#1890ff" }} />
                    {t("quotation.date") || "日期"}
                  </label>
                  <DatePicker
                    value={date ? dayjs(date) : null}
                    onChange={(e) =>
                      handleDataChange("date", e ? e.format("YYYY-MM-DD") : "")
                    }
                    placeholder={t("quotation.datePlaceholder") || "請選擇日期"}
                    className="h-9 w-full"
                  />
                </div>
                <div className="space-y-1.5">
                  <label
                    className="text-xs font-semibold flex items-center gap-1.5"
                    style={{ color: "#6F6F6F" }}
                  >
                    <IconUser size={14} style={{ color: "#1890ff" }} />
                    {t("quotation.partner") || "合作方"}
                  </label>
                  <Select
                    value={partner?.id ? String(partner.id) : null}
                    onChange={(value) => {
                      const selected = partnerData.find(
                        (p) => String(p?.id) === value
                      );
                      handleDataChange("partner", selected ?? null);
                    }}
                    placeholder={
                      t("quotation.partnerPlaceholder") || "請選擇合作方"
                    }
                    leftSection={<IconUser size={16} />}
                    data={partnerData?.map((p) => ({
                      value: String(p?.id),
                      label: `${p?.name} (ID: ${p?.id})`,
                    }))}
                    searchable
                    className="h-9"
                  />
                </div>
              </div>

              <div className="pt-3 border-t">
                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <IconMapPin size={16} style={{ color: "#52c41a" }} />
                  {t("quotation.addressInfo") || "地址資訊"}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-1.5">
                    <label
                      className="text-xs font-semibold flex items-center gap-1.5"
                      style={{ color: "#6F6F6F" }}
                    >
                      <IconMapPin size={14} style={{ color: "#52c41a" }} />
                      {t("quotation.city") || "縣市"}
                    </label>
                    <Input
                      value={city}
                      onChange={(e) =>
                        handleChangeAddressData(e.target.value, "city")
                      }
                      placeholder={
                        t("quotation.cityPlaceholder") || "請輸入縣市"
                      }
                      className="h-9"
                      leftSection={<IconMapPin size={16} />}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label
                      className="text-xs font-semibold flex items-center gap-1.5"
                      style={{ color: "#6F6F6F" }}
                    >
                      <IconMapPin size={14} style={{ color: "#52c41a" }} />
                      {t("quotation.district") || "行政區"}
                    </label>
                    <Input
                      value={district}
                      onChange={(e) =>
                        handleChangeAddressData(e.target.value, "district")
                      }
                      placeholder={
                        t("quotation.districtPlaceholder") || "請輸入行政區"
                      }
                      className="h-9"
                      leftSection={<IconMapPin size={16} />}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label
                      className="text-xs font-semibold flex items-center gap-1.5"
                      style={{ color: "#6F6F6F" }}
                    >
                      <IconMapPin size={14} style={{ color: "#52c41a" }} />
                      {t("quotation.postalCode") || "郵遞區號"}
                    </label>
                    <Input
                      value={postalCode}
                      onChange={(e) =>
                        handleChangeAddressData(e.target.value, "postalCode")
                      }
                      placeholder={
                        t("quotation.postalCodePlaceholder") || "請輸入郵遞區號"
                      }
                      className="h-9"
                      leftSection={<IconMapPin size={16} />}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label
                      className="text-xs font-semibold flex items-center gap-1.5"
                      style={{ color: "#6F6F6F" }}
                    >
                      <IconMapPin size={14} style={{ color: "#52c41a" }} />
                      {t("quotation.fullAddress") || "完整地址"}
                    </label>
                    <Input
                      value={address}
                      onChange={(e) =>
                        handleChangeAddressData(e.target.value, "address")
                      }
                      placeholder={
                        t("quotation.fullAddressPlaceholder") ||
                        "請輸入完整地址"
                      }
                      className="h-9"
                      leftSection={<IconMapPin size={16} />}
                    />
                  </div>
                </div>
              </div>

              <div className="pt-3 border-t">
                <label
                  className="text-xs font-semibold flex items-center gap-1.5 mb-2"
                  style={{ color: "#6F6F6F" }}
                >
                  <IconFileText size={14} />
                  {t("quotation.remarks") || "備註"}
                </label>
                <AntdInput.TextArea
                  value={remarks}
                  onChange={(e) => handleDataChange("remarks", e.target.value)}
                  placeholder={
                    t("quotation.remarksPlaceholder") || "請輸入備註資訊"
                  }
                  className="min-h-[100px] resize-none"
                />
              </div>
              <div className="flex justify-end gap-4">
                <Button
                  size="large"
                  onClick={() => navigate(`/hrMainPage/projectQuotation/${id}`)}
                  className="px-8"
                >
                  {t("quotation.cancel") || "取消"}
                </Button>
                <Button
                  type="primary"
                  size="large"
                  className="px-8"
                  onClick={() => {
                    const submissionData = {
                      name: quotationData.name,
                      date: quotationData.date,
                      remarks: quotationData.remarks || "",
                      partnerAddress: {
                        city: quotationData.partnerAddress?.city || "",
                        district: quotationData.partnerAddress?.district || "",
                        postalCode:
                          quotationData.partnerAddress?.postalCode || 0,
                        address: quotationData.partnerAddress?.address || "",
                      },
                      attachment: Array.isArray(newFileUpload)
                        ? newFileUpload
                        : [],
                      contractFile:
                        Array.isArray(contractFileUpload) &&
                        contractFileUpload.length > 0
                          ? contractFileUpload[0]
                          : null,
                    };
                    handleSubmit({ data: submissionData });
                  }}
                >
                  {t("quotation.submitEdit") || "送出編輯"}
                </Button>
              </div>
            </div>
          )}
        </Card>

        <Card className="mb-6 shadow-lg">
          <div className="space-y-6 p-6">
            <div className="flex items-center gap-3 mb-4">
              <IconPackage size={20} style={{ color: "#1890ff" }} />
              <h2 className="text-xl font-semibold" style={{ color: "#6F6F6F" }}>
                {t("productionPlan.order") || "訂單"}
              </h2>
            </div>

            <div>
              <h3
                className="text-xl font-semibold mb-4 flex items-center gap-2"
                style={{ color: "#6F6F6F" }}
              >
                <IconPackage size={20} />
                {t("productionPlan.bomTable") || "BOM表"}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Button
                  size="large"
                  className="h-14 flex items-center justify-center gap-2 text-base font-medium"
                  onClick={() => navigate(`/hrMainPage/steelAssemblyBOM/${id}`)}
                >
                  {t("hrMain.materialBOM") || "材料BOM"}
                  <IconArrowRight size={16} />
                </Button>
                <Button
                  size="large"
                  className="h-14 flex items-center justify-center gap-2 text-base font-medium"
                  onClick={() => navigate(`/hrMainPage/steelBoltBOM/${id}`)}
                >
                  {t("hrMain.boltBOM") || "螺絲BOM"}
                  <IconArrowRight size={16} />
                </Button>
              </div>
            </div>

            <div className="border-t pt-6">
              <div className="flex items-center gap-3 mb-4">
                <IconFileText size={20} style={{ color: "#1890ff" }} />
                <h2 className="text-xl font-semibold" style={{ color: "#6F6F6F" }}>
                  {t("productionPlan.production") || "生產"}
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Button
                  size="large"
                  className="h-14 flex items-center justify-center gap-2 text-base font-medium"
                  onClick={() => navigate(`/hrMainPage/designBOM/${id}`)}
                >
                  {t("productionPlan.designBOM") || "設計BOM"}
                  <IconArrowRight size={16} />
                </Button>
                <Button
                  size="large"
                  className="h-14 flex items-center justify-center gap-2 text-base font-medium"
                  onClick={() => window.open(`/productionPlan/${id}`, "_blank")}
                >
                  {t("productionPlan.productionPlanTable") || "生產計畫表"}
                  <IconArrowRight size={16} />
                </Button>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3
                className="text-xl font-semibold mb-4 flex items-center gap-2"
                style={{ color: "#6F6F6F" }}
              >
                <IconUpload size={20} />
                {t("quotation.fileUpload") || "公務檔案"}
              </h3>

              {currentFolder === null ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <Upload
                    name="avatar"
                    showUploadList={false}
                    multiple
                    onChange={handleFirstLayerUpload}
                    className="w-full h-full"
                  >
                    <Spin spinning={fileUploadState} className="h-full">
                      <div className="border-2 border-dashed rounded-xl p-8 hover:border-blue-400 transition-all cursor-pointer h-full">
                        <div className="flex flex-col items-center justify-center text-center gap-3 h-full">
                          <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center">
                            <IconUpload size={32} style={{ color: "#1890ff" }} />
                          </div>
                          <div>
                            <p className="font-semibold" style={{ color: "#6F6F6F" }}>
                              {t("quotation.clickToUpload") || "點擊上傳檔案"}
                            </p>
                            <p className="text-sm text-gray-400">
                              {t("quotation.supportedFormats") || "支援各種文件格式"}
                            </p>
                          </div>
                          <Button className="mt-2">
                            {t("quotation.addFile") || "新增檔案"}
                          </Button>
                        </div>
                      </div>
                    </Spin>
                  </Upload>
                  {FOLDERS.map((folder) => {
                    const fileCount = getFilesInFolder(folder.id).length;
                    return (
                      <div
                        key={folder.id}
                        onClick={() => setCurrentFolder(folder.id)}
                        className="border-2 border-solid rounded-xl p-8 hover:border-blue-400 transition-all cursor-pointer bg-white"
                      >
                        <div className="flex flex-col items-center justify-center text-center gap-3 h-full">
                          <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center">
                            <IconFolder size={32} style={{ color: "#1890ff" }} />
                          </div>
                          <p className="font-semibold text-lg" style={{ color: "#6F6F6F" }}>
                            {folder.name}
                          </p>
                          <p className="text-sm text-gray-400">
                            {fileCount} {t("quotation.files") || "個檔案"}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <Button
                      icon={<IconArrowLeft size={16} />}
                      onClick={() => setCurrentFolder(null)}
                    >
                      {t("quotation.backToFolders") || "返回資料夾"}
                    </Button>
                    <span className="text-lg font-semibold" style={{ color: "#6F6F6F" }}>
                      {FOLDERS.find((f) => f.id === currentFolder)?.name || currentFolder}
                    </span>
                    <Upload
                      name="file"
                      showUploadList={false}
                      multiple
                      beforeUpload={(file) => { handleFolderUpload(file); return false; }}
                    >
                      <Spin spinning={fileUploadState}>
                        <Button icon={<IconUpload size={16} />}>
                          {t("quotation.addFile") || "新增檔案"}
                        </Button>
                      </Spin>
                    </Upload>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {getFilesInFolder(currentFolder).length === 0 && (
                      <div className="col-span-full text-center text-gray-400 py-8">
                        {t("quotation.noFiles") || "此資料夾沒有檔案"}
                      </div>
                    )}
                    {getFilesInFolder(currentFolder).map((file: any) => (
                      <div
                        key={file?.id}
                        className="border-2 rounded-xl p-8 hover:border-blue-400 transition-all min-h-[200px]"
                      >
                        <div className="flex flex-col items-center justify-center text-center gap-3 h-full">
                          <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center">
                            <IconFileText size={32} style={{ color: "#1890ff" }} />
                          </div>
                          <div>
                            <p
                              className="font-semibold truncate max-w-[200px]"
                              style={{ color: "#6F6F6F" }}
                              title={file?.name}
                            >
                              {file?.name}
                            </p>
                          </div>
                          <Button
                            type="link"
                            danger
                            className="mt-2"
                            onClick={async () => {
                              const updatedFiles = newFileUpload.filter(
                                (f: any) => f.id !== file?.id
                              );
                              setNewFileUpload(updatedFiles);
                              try {
                                await single(
                                  orpcClient.project.update([
                                    { id: id!, attachment: updatedFiles },
                                  ])
                                );
                                api.success({
                                  message: t("quotation.deleteSuccessTitle"),
                                  description: <div>{t("quotation.fileDeletedSuccessfully")}</div>,
                                  placement: "topLeft",
                                  duration: 2000,
                                });
                              } catch (error) {
                                console.error("Error deleting file:", error);
                                api.error({
                                  message: t("quotation.deleteErrorTitle"),
                                  description: (
                                    <div>{(error as any)?.message || t("quotation.fileDeleteFailed")}</div>
                                  ),
                                  placement: "topLeft",
                                  duration: 3000,
                                });
                              }
                            }}
                          >
                            {t("quotation.delete") || "刪除"}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="border-t pt-6">
              <h3
                className="text-xl font-semibold mb-4 flex items-center gap-2"
                style={{ color: "#6F6F6F" }}
              >
                <IconFileText size={20} />
                {t("quotation.contractUpload") || "財務檔案"}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <Upload
                  name="contract"
                  showUploadList={false}
                  onChange={async (info) => {
                    await handleChangeContractImg(info);
                  }}
                  className="w-full"
                >
                  <Spin spinning={contractUploadState}>
                    <div className="border-2 border-dashed rounded-xl p-8 hover:border-blue-400 transition-all cursor-pointer min-h-[200px]">
                      <div className="flex flex-col items-center justify-center text-center gap-3 h-full">
                        <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center">
                          <IconFileText size={32} style={{ color: "#52c41a" }} />
                        </div>
                        <div>
                          <p className="font-semibold" style={{ color: "#6F6F6F" }}>
                            {t("quotation.viewContract") || "上傳合約文件"}
                          </p>
                          <p className="text-sm text-gray-400">
                            {t("quotation.supportedContractFormats") ||
                              "支援 PDF、Word 等格式"}
                          </p>
                        </div>
                        <Button className="mt-2">
                          {t("quotation.addFile") || "新增檔案"}
                        </Button>
                      </div>
                    </div>
                  </Spin>
                </Upload>

                {Array.isArray(contractFileUpload) &&
                  contractFileUpload.map((file) => (
                    <div
                      key={file?.id}
                      className="border-2 border-solid rounded-xl p-8 hover:border-blue-400 transition-all min-h-[200px] bg-white"
                    >
                      <div className="flex flex-col items-center justify-center text-center gap-3 h-full">
                        <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center">
                          <IconFileText size={32} style={{ color: "#1890ff" }} />
                        </div>
                        <div>
                          <p
                            className="font-semibold truncate max-w-[200px]"
                            style={{ color: "#6F6F6F" }}
                            title={file?.name}
                          >
                            {file?.name}
                          </p>
                        </div>
                        <Button
                          type="link"
                          danger
                          className="mt-2"
                          onClick={async () => {
                            setContractFileUpload([]);
                            try {
                              await single(
                                orpcClient.project.update([
                                  { id: id!, contractFile: null },
                                ])
                              );
                              api.success({
                                message: t("quotation.deleteSuccessTitle"),
                                description: <div>{t("quotation.contractDeletedSuccessfully")}</div>,
                                placement: "topLeft",
                                duration: 2000,
                              });
                            } catch (error) {
                              console.error("Error deleting contract:", error);
                              api.error({
                                message: t("quotation.deleteErrorTitle"),
                                description: (
                                  <div>
                                    {(error as any)?.message || t("quotation.contractDeleteFailed")}
                                  </div>
                                ),
                                placement: "topLeft",
                                duration: 3000,
                              });
                            }
                          }}
                        >
                          {t("quotation.delete") || "刪除"}
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

          </div>
        </Card>
      </div>

      <Modal
        title={t("quotation.selectFolder") || "選擇資料夾"}
        open={folderSelectModalOpen}
        onCancel={() => {
          setFolderSelectModalOpen(false);
          setPendingUploadFiles([]);
        }}
        footer={null}
        width={600}
      >
        <p className="mb-4 text-gray-500">
          {t("quotation.selectFolderDescription") || "請選擇要上傳到哪個資料夾"}
        </p>
        <div className="grid grid-cols-2 gap-3">
          {FOLDERS.map((folder) => (
            <div
              key={folder.id}
              onClick={() => handleModalFolderSelectAndUpload(folder.id)}
              className="border-2 border-solid rounded-xl p-4 hover:border-blue-400 hover:bg-blue-50 transition-all cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                  <IconFolder size={20} style={{ color: "#1890ff" }} />
                </div>
                <p className="font-semibold" style={{ color: "#6F6F6F" }}>
                  {folder.name}
                </p>
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}

export default EditQuotationPage;
